// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import * as THREE from 'three';

import type { PointCloudScene } from './PointCloudScene';
import { FACE_HANDLES, boxToPlanes, dragFace, handleCenter } from './clippingBox';

/**
 * Three.js scene layer for the interactive clipping box (#444).
 *
 * Draws a wireframe box outline and six spherical face handles in the scene.
 * When a handle is dragged the corresponding face moves, the six renderer
 * clipping planes update live, and `onChange` is called on pointer-up so the
 * React state holds the box.
 *
 * Two independent switches: `setToolVisible` shows the box and its handles
 * (and only then do they take the pointer), `setActive` turns the cut on or
 * off. The cut needs a box: one set from React (`setBox`) or dragged here.
 * Until then the tool sits on the scene bounds and cuts nothing, so showing
 * the tool alone never hides a point still streaming in past those bounds.
 *
 * All coordinates are in scene-local space (world − recentring origin), the
 * same frame every other object in the scene inhabits. The box and handles
 * draw in the host's overlay, which the renderer's clipping planes skip — a
 * handle sits exactly on the clip boundary, so in the main scene it would be
 * cut in half or vanish (frontend-h1x).
 */

/** Handle sphere colour — accent blue. */
const HANDLE_COLOR = 0x6ea8fe;
/** Wireframe box colour — dimmed accent. */
const WIRE_COLOR = 0x3b5a8a;
/** Handle colour while dragging. */
const HANDLE_DRAG_COLOR = 0xffa500;
/** Handle sphere radius in metres. */
const HANDLE_BASE_RADIUS = 0.08;

export class ClippingBoxLayer {
  private readonly host: PointCloudScene;
  private readonly canvas: HTMLCanvasElement;
  private readonly onChange: (box: THREE.Box3) => void;

  private readonly group = new THREE.Group();
  private readonly handles: THREE.Mesh[] = [];
  private wireframe: THREE.LineSegments | null = null;

  private box = new THREE.Box3();
  /** `box` is a real box (from React, or dragged), not the scene bounds the tool starts on. */
  private hasBox = false;
  private toolVisible = false;
  private active = false;

  // Drag state
  private drag: {
    pointerId: number;
    handleIndex: number;
    dragPlane: THREE.Plane;
    startBox: THREE.Box3;
    startHit: THREE.Vector3;
  } | null = null;

  constructor(host: PointCloudScene, onChange: (box: THREE.Box3) => void) {
    this.host = host;
    this.canvas = host.getCanvas();
    this.onChange = onChange;

    this.buildHandles();
    this.group.visible = false;
    host.overlayRoot().add(this.group);

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
  }

  // --- public API ----------------------------------------------------------

  /**
   * Show or hide the box and its face handles. Hidden, they ignore the
   * pointer (orbiting is never blocked); shown without a box of its own, the
   * tool starts on the current scene bounds.
   */
  setToolVisible(visible: boolean): void {
    if (visible === this.toolVisible) return;
    this.toolVisible = visible;
    if (visible) {
      if (!this.hasBox) this.initFromSceneBounds();
    } else {
      this.cancelDrag();
    }
    this.group.visible = visible;
  }

  /** Turn the cut on or off; it applies only once there is a box. */
  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    this.syncPlanes();
  }

  /** The box from React, or null for none (the whole model: the tool goes back to the scene bounds). */
  setBox(nextBox: THREE.Box3 | null): void {
    if (nextBox === null) {
      if (!this.hasBox) return;
      this.cancelDrag();
      this.hasBox = false;
      if (this.toolVisible) this.initFromSceneBounds();
      this.syncPlanes();
      return;
    }
    if (this.hasBox && this.box.equals(nextBox)) return;
    this.hasBox = true;
    this.box.copy(nextBox);
    this.rebuildWireframe();
    this.updateHandlePositions();
    this.syncPlanes();
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);

    this.host.setClippingPlanes([]);
    this.host.setControlsEnabled(true);
    this.host.overlayRoot().remove(this.group);
    this.disposeGroup();
  }

  // --- scene construction --------------------------------------------------

  private buildHandles(): void {
    const geo = new THREE.SphereGeometry(1, 8, 6);
    for (let i = 0; i < FACE_HANDLES.length; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.userData['handleIndex'] = i;
      this.handles.push(mesh);
      this.group.add(mesh);
    }
  }

  private rebuildWireframe(): void {
    if (this.wireframe) {
      this.group.remove(this.wireframe);
      this.wireframe.geometry.dispose();
      (this.wireframe.material as THREE.Material).dispose();
      this.wireframe = null;
    }
    const size = this.box.getSize(new THREE.Vector3());
    const center = this.box.getCenter(new THREE.Vector3());
    const boxGeo = new THREE.BoxGeometry(size.x, size.y, size.z);
    const edgesGeo = new THREE.EdgesGeometry(boxGeo);
    boxGeo.dispose();
    const mat = new THREE.LineBasicMaterial({ color: WIRE_COLOR });
    this.wireframe = new THREE.LineSegments(edgesGeo, mat);
    this.wireframe.position.copy(center);
    this.group.add(this.wireframe);
  }

  private updateHandlePositions(): void {
    const radius = this.handleRadius();
    for (let i = 0; i < FACE_HANDLES.length; i++) {
      const c = handleCenter(FACE_HANDLES[i], this.box);
      this.handles[i].position.copy(c);
      this.handles[i].scale.setScalar(radius);
    }
  }

  private handleRadius(): number {
    return Math.max(HANDLE_BASE_RADIUS, this.box.getBoundingSphere(new THREE.Sphere()).radius * 0.03);
  }

  // --- internal helpers ----------------------------------------------------

  private initFromSceneBounds(): void {
    const sceneBounds = this.host.getSceneBounds();
    if (sceneBounds && !sceneBounds.isEmpty()) {
      this.box.copy(sceneBounds);
    } else {
      // Fallback when no content is loaded yet
      this.box.set(new THREE.Vector3(-5, -5, -2), new THREE.Vector3(5, 5, 3));
    }
    this.rebuildWireframe();
    this.updateHandlePositions();
  }

  /**
   * The renderer's clipping planes: the box's six when the cut is on and
   * there is a box — a dragged one counts from the first move — else none.
   */
  private syncPlanes(): void {
    this.host.setClippingPlanes(this.active && (this.hasBox || this.drag !== null) ? boxToPlanes(this.box) : []);
  }

  /**
   * Drop a drag in progress (the tool hidden, or the box reset under it):
   * the box goes back to where the drag started, nothing is reported, and
   * orbiting is given back.
   */
  private cancelDrag(): void {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    const mat = this.handles[drag.handleIndex].material as THREE.MeshBasicMaterial;
    mat.color.setHex(HANDLE_COLOR);
    if (this.canvas.hasPointerCapture(drag.pointerId)) this.canvas.releasePointerCapture(drag.pointerId);
    this.host.setControlsEnabled(true);
    this.box.copy(drag.startBox);
    this.rebuildWireframe();
    this.updateHandlePositions();
    this.syncPlanes();
  }

  private ndcFromEvent(event: PointerEvent): THREE.Vector2 {
    const rect = this.canvas.getBoundingClientRect();
    return new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private raycastPlane(ndc: THREE.Vector2, plane: THREE.Plane): THREE.Vector3 | null {
    const camera = this.host.getCamera();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    const target = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, target);
  }

  private disposeGroup(): void {
    for (const mesh of this.handles) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    if (this.wireframe) {
      this.wireframe.geometry.dispose();
      (this.wireframe.material as THREE.Material).dispose();
    }
    this.group.clear();
  }

  // --- pointer event handlers ----------------------------------------------

  private readonly onPointerDown = (event: PointerEvent): void => {
    // Hidden handles are still raycastable: ignore them, so orbiting is never blocked.
    if (!this.toolVisible || this.drag || event.button !== 0) return;

    const ndc = this.ndcFromEvent(event);
    const hits = this.host.pick(ndc, this.handles);
    if (hits.length === 0) return;

    const hitMesh = hits[0].object as THREE.Mesh;
    const handleIndex = hitMesh.userData['handleIndex'] as number;
    if (handleIndex === undefined) return;

    event.stopPropagation();
    this.canvas.setPointerCapture(event.pointerId);
    this.host.setControlsEnabled(false);

    // Build a drag plane facing the camera through the handle center
    const camera = this.host.getCamera();
    const camDir = new THREE.Vector3();
    camera.getWorldDirection(camDir);
    const planeNormal = camDir.negate().normalize();
    const center = handleCenter(FACE_HANDLES[handleIndex], this.box);
    const dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, center);

    const startHit = this.raycastPlane(ndc, dragPlane) ?? center;
    this.drag = { pointerId: event.pointerId, handleIndex, dragPlane, startBox: this.box.clone(), startHit };

    // Visual: highlight dragged handle
    const mat = hitMesh.material as THREE.MeshBasicMaterial;
    mat.color.setHex(HANDLE_DRAG_COLOR);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    const ndc = this.ndcFromEvent(event);
    const hit = this.raycastPlane(ndc, this.drag.dragPlane);
    if (!hit) return;

    const delta = hit.sub(this.drag.startHit);
    this.box.copy(dragFace(this.drag.startBox, FACE_HANDLES[this.drag.handleIndex], delta));
    this.rebuildWireframe();
    this.updateHandlePositions();
    this.syncPlanes();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    // Restore handle colour
    const mat = this.handles[drag.handleIndex].material as THREE.MeshBasicMaterial;
    mat.color.setHex(HANDLE_COLOR);

    this.drag = null;
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    this.host.setControlsEnabled(true);

    // A click on a handle that moved nothing makes no box.
    if (drag.startBox.equals(this.box)) {
      this.syncPlanes();
      return;
    }
    this.hasBox = true;
    this.syncPlanes();
    // React holds the box from here (`setBox` with the same box is a no-op).
    this.onChange(this.box.clone());
  };
}
